import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ISelectOption } from 'app/services/api5-service/api.interface';
import InputMask from 'inputmask';
import { distinctUntilChanged, pairwise } from 'rxjs/operators';

import { filterDefined } from 'utils/is-defined';
import { RequiredField } from 'utils/required-field.type';

import {
  appendCodeToPhone,
  isPhoneBelongsToCode,
  purifyPhoneFromMaskValue,
  replacePhoneCodeInPhone,
} from './phone.utils';

import { ISPFieldType } from '../../model';
import { ISPFieldTypeBase } from '../field-type-base.class';

/**
 * Prefixed select-input group for phone field Formly field
 */
@UntilDestroy()
@Component({
  selector: 'isp-formly-phone-field',
  templateUrl: './phone.component.html',
  styleUrls: ['./scss/phone.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhoneComponent
  extends ISPFieldTypeBase<ISPFieldType.Phone>
  implements OnInit
{
  private lastTimeSelectClosed: number;

  /** Input mask controller */
  private mask?: InputMask.Instance;

  isDropdownOpened: boolean;

  isSelectFocused: boolean;

  @ViewChild('inputElement', { static: true })
  inputRef: ElementRef<HTMLInputElement>;

  selectControl: FormControl = new FormControl();

  private setMask(inputControl: HTMLInputElement, mask?: string): void {
    this.mask = InputMask({
      mask,
      showMaskOnHover: false,
      placeholder: '_',
    });
    this.mask.mask(inputControl);
  }

  private removeMask(): void {
    this.mask?.remove();
    this.mask = undefined;
  }

  private updateInputMask(mask: string): void {
    if (!mask) {
      this.removeMask();
      return;
    }

    if (!this.mask) {
      this.setMask(this.inputRef.nativeElement, mask);
    } else {
      // keep cursor position on the same place
      const prevCursorPosition = this.inputRef.nativeElement.selectionStart;
      this.removeMask();
      this.setMask(this.inputRef.nativeElement, mask);
      this.inputRef.nativeElement.setSelectionRange(
        prevCursorPosition,
        prevCursorPosition,
      );
    }
  }

  private updateInputMaskValue(unmaskedValue?: string): void {
    if (this.mask && unmaskedValue !== undefined) {
      this.mask.setValue(unmaskedValue);
      this.inputRef.nativeElement.value = unmaskedValue;

      // @HACK disable async validators, because BE return value without mask placeholder, but mask apply value with placeholder.
      // this starts infinite validation loop. So, in order to prevent it - disable validation on that moments
      const validators = this.formControl.asyncValidator;
      this.formControl.clearAsyncValidators();
      this.formControl.setValue(unmaskedValue);
      this.formControl.setAsyncValidators(validators);
    }
  }

  /**
   * Get next input value after select value change
   *
   * @param prevOption - prev select option
   * @param nextOption - next select option
   * @returns next input value or null, if no next value is needed
   */
  private getInputValueAfterSelectChanges(
    prevOption: ISelectOption | undefined,
    nextOption: ISelectOption,
  ): string | null {
    const prevInputValue = this.inputRef.nativeElement.value;
    const prevInputValuePurified = purifyPhoneFromMaskValue(prevInputValue);
    const isOptionsChanges =
      prevOption && prevOption.$code !== nextOption.$code;
    const prevOptionCode = prevOption?.$code;
    const nextOptionCode = nextOption.$code || '';

    if (
      isOptionsChanges &&
      prevOptionCode &&
      nextOptionCode &&
      isPhoneBelongsToCode(prevInputValuePurified, prevOptionCode)
    ) {
      return replacePhoneCodeInPhone(
        prevInputValuePurified,
        prevOptionCode,
        nextOptionCode,
      );
    } else {
      if (!prevInputValuePurified.startsWith(nextOptionCode)) {
        const phoneWithoutCode = prevInputValuePurified.replace('+', '');
        return `${nextOptionCode}${phoneWithoutCode}`;
      } else {
        return null;
      }
    }
  }

  /**
   * Update input config, after select value changes
   *
   * @param nextOption - next selected option
   */
  private updateInputMaskFromSelectedOption(nextOption: ISelectOption): void {
    // update input mask, from selected option
    const mask = nextOption.$mask;
    this.updateInputMask(mask!);
  }

  /**
   * Append phone country to input, if input is empty
   *
   * @param value - input value
   * @param option - country selected option
   */
  private appendPhoneCountryIfNeeded(
    value: string,
    option?: ISelectOption,
  ): void {
    if (option?.$code && !value.startsWith(option.$code)) {
      const nextInputValue = appendCodeToPhone(value, option.$code);
      this.updateInputMaskValue(nextInputValue);
    }
  }

  /**
   * Handle any select control change, either by form or by user. Change related input value
   *
   * @param prevOption - selected option
   * @param nextOption - selected option
   * @param changedByUser - is value was changed by user, not by some form process like setValue
   */
  private onSelectChange(
    prevOption: ISelectOption | undefined,
    nextOption: ISelectOption,
    changedByUser?: boolean,
  ): void {
    this.updateInputMaskFromSelectedOption(nextOption);

    // wait until mask will changes, and then updates input value
    setTimeout(() => {
      const nextInputValue = this.getInputValueAfterSelectChanges(
        prevOption,
        nextOption,
      );
      if (nextInputValue !== null) {
        this.updateInputMaskValue(nextInputValue);
      }
    });

    if (changedByUser) {
      // use setTimeout to wait, until input field will be displayed again, after 'display: none'
      setTimeout(() => {
        this.inputRef.nativeElement.focus();
      });
    }
  }

  ngOnInit(): void {
    super.ngOnInit();

    if (this.props.originalControl.$mask) {
      this.setMask(
        this.inputRef.nativeElement,
        this.props.originalControl.$mask,
      );
    }
    this.selectControl.setValue(this.props.selectCountryDefaultValue);
    this.form.addControl(this.props.selectCountry.$name, this.selectControl);

    this.formState.validationService
      .getNotificationAfterValidationPatch(this.props.originalControl.$name)
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        this.updateInputMaskValue(purifyPhoneFromMaskValue(value));
      });

    this.formState.disabledService
      .isFieldDisabled$(this.props.originalControl.$name)
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe(isDisabled => {
        if (isDisabled) {
          this.formState.disabledService.disableControl(
            this.props.selectCountry.$name,
          );
          this.selectControl.disable();
        } else {
          this.formState.disabledService.enableControl(
            this.props.selectCountry.$name,
          );
          this.selectControl.enable();
        }
      });

    this.formState.selectService
      .getSelectedOptionForSelect$(this.props.selectCountry.$name)
      .pipe(
        filterDefined(),
        distinctUntilChanged((prev, next) => prev.$code === next.$code),
        pairwise(),
        untilDestroyed(this),
      )
      .subscribe(([prevOption, nextOption]) => {
        if (!nextOption) {
          return;
        }

        // @HACK this is a way, to determine user selection change.
        // If select dropdown was closed a few moment ago AND selected option changes I assume,
        // that this was user action. But this is strange hack...
        const isOptionWasChangedByUser =
          new Date().getTime() - this.lastTimeSelectClosed < 50;
        this.onSelectChange(prevOption, nextOption, isOptionWasChangedByUser);
      });
  }

  onInternalAction(
    event: CustomEvent<{
      name: 'dropdown-closing-start' | 'dropdown-opening-start';
    }>,
  ): void {
    switch (event.detail.name) {
      case 'dropdown-closing-start':
        this.isDropdownOpened = false;
        this.lastTimeSelectClosed = new Date().getTime();
        break;
      case 'dropdown-opening-start':
        this.isDropdownOpened = true;
        this.cdr.detectChanges();
        break;
      default:
    }
  }

  setSelectFocused(state: boolean): void {
    this.isSelectFocused = state;
  }

  /**
   * Handle input value change. Change related select value
   *
   * @HACK use native input element to detect value changes. Do not use input formControl, cause it works with 'onBlur' mode
   * and it should stay in that mode!
   * @param event
   */
  onInputChange(event: InputEvent): void {
    const value = (event.target as HTMLInputElement).value;
    if (!value) {
      return;
    }

    const purifiedValue = purifyPhoneFromMaskValue(value);
    if (purifiedValue === '+') {
      return;
    }

    const selectOptions = this.formState.selectService.getOptionsForSelect(
      this.props.selectCountry.$name,
    ) as RequiredField<ISelectOption, '$code'>[];

    const sortedSelectOptions = [...selectOptions].sort((a, b) =>
      a.$code.length > b.$code.length ? -1 : 1,
    );
    const firstMatchSelectOption = sortedSelectOptions.find(option =>
      purifiedValue.startsWith(option.$code),
    );

    if (!firstMatchSelectOption) {
      return;
    }

    const selectedOption =
      this.formState.selectService.getSelectedOptionForSelect(
        this.props.selectCountry.$name,
      );

    if (
      selectedOption?.$key !== firstMatchSelectOption.$key &&
      selectedOption?.$code !== firstMatchSelectOption.$code
    ) {
      this.selectControl.setValue(firstMatchSelectOption.$key);
    }
  }

  /**
   * Handle input blur for code autofill
   */
  onInputBlur(): void {
    const selectedOption =
      this.formState.selectService.getSelectedOptionForSelect(
        this.props.selectCountry.$name,
      );
    const value = this.inputRef.nativeElement.value;
    this.appendPhoneCountryIfNeeded(value, selectedOption);
  }
}
